/**
 * Study-export wire contract (PRD H2; increment-9 plan §1 PR6).
 *
 * `GET /api/episodes/:episodeId/export` — the WIRE for the projection that
 * already exists in ./studyExport.ts. Kept in its own file precisely so the
 * adversarially-tested projection is not edited to add a route: the shape
 * and the redaction rules are PR4's and settled, and this is only how a
 * caller asks for them.
 *
 * THE RESPONSE BODY IS THE EXPORT DOCUMENT ITSELF — the nine top-level keys
 * of `studyExportSchema`, with no `{ ok: true, … }` envelope, which is the
 * one place this API departs from its own house shape. The reason is the
 * contract: plan §0.1a fixes the export AS the Patient-001 dataset's nine
 * keys and §3 requires the report to render an exported episode "without
 * modification". A tenth key on the wire would make that false at the wire,
 * and every fact an envelope would have carried (episode id, exported-at,
 * profile, schema version) is already inside `meta.platformRecord`. Callers
 * branch on HTTP status, as the physio-app client already does; refusals
 * still speak the normal `{ ok: false, code, message }` contract.
 *
 * THE CONTENT HASH THE ROUTE LOGS IS OVER THE BYTES IT SENDS — the body is
 * serialized once, hashed, and that same string is what leaves. A hash of a
 * re-serialization would identify a file nobody received.
 */

import { z } from 'zod';

import { studyExportProfileSchema } from './studyExport';

/**
 * CALL 4's DEFAULT, NAMED ONCE. Free text is excluded by default and
 * includable only by an explicit choice, so `?profile=` omitted means
 * `pii-redacted` and `full-text-unscreened` must be asked for BY NAME.
 *
 * It lives here as a named constant rather than as a literal in the route so
 * that the export surface (PR7) reads the same fact instead of restating it.
 * A second reading of a settled fact is how PR4's `freeTextIncluded` bug got
 * in (plan §0.1c) and how §1c's second `uploadState` derivation nearly did.
 */
export const DEFAULT_STUDY_EXPORT_PROFILE = 'pii-redacted' as const;

/**
 * Query for the export route.
 *
 * OMISSION is the only path to the default: an unrecognised `profile` is
 * refused as `invalid_request` rather than quietly defaulted, so a caller
 * who mistypes learns it. Both failure directions are safe — a typo can
 * never yield the unscreened profile, and it can never silently yield the
 * redacted one either.
 */
export const studyExportQuerySchema = z.object({
  profile: studyExportProfileSchema.default(DEFAULT_STUDY_EXPORT_PROFILE),
});
export type StudyExportQuery = z.infer<typeof studyExportQuerySchema>;

/**
 * The `access_log` detail written by `export.generated` (plan §0.1a: "who
 * exported what, when" must be answerable about the export as well as about
 * the media). `profile` is there because call 4 requires the free-text
 * choice to be recorded in that row.
 */
export interface ExportGeneratedDetail {
  readonly profile: string;
  /** sha256, hex, over the exact response bytes — `sha256:<hex>`. */
  readonly content_hash: string;
  readonly content_bytes: number;
  readonly schema_version: string;
  readonly study_code: string;
}
